import { useState, useContext } from "react";
import "./EditProfilePopup.css";
import { ThemeContext } from "../context/themeContext";

export default function EditProfilePopup({ user, onClose, onSave }) {
  const [name, setName] = useState(user?.name?.replace(" Pvt. Ltd.", "") || "");
  const [email, setEmail] = useState(user?.email || "");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });

  const { activeTheme } = useContext(ThemeContext);
  const isDark = activeTheme === "dark";

  const emailChanged = email !== user?.email;

  /* ── Save name only ── */
  const handleSaveName = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim() });
    setMsg({ text: "Name saved!", type: "success" });
    setTimeout(() => setMsg({ text: "", type: "" }), 2000);
  };

  /* ── Send OTP ── */
  const handleSendOtp = async () => {
    if (!email || !email.includes("@")) {
      setMsg({ text: "Please enter a valid email.", type: "error" });
      return;
    }
    if (!emailChanged) {
      setMsg({ text: "New email is same as current.", type: "error" });
      return;
    }
    setEmailLoading(true);
    setMsg({ text: "", type: "" });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/send-email-otp`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newEmail: email, userId: user?.user_id }),
      });
      const data = await res.json();
      if (res.ok) {
        setOtpSent(true);
        setMsg({ text: "", type: "" });
      } else {
        setMsg({ text: data.error || "Failed to send OTP.", type: "error" });
      }
    } catch {
      setMsg({ text: "Server error. Try again.", type: "error" });
    } finally {
      setEmailLoading(false);
    }
  };

  /* ── Verify OTP ── */
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setMsg({ text: "Please enter the 6-digit OTP.", type: "error" });
      return;
    }
    setEmailLoading(true);
    setMsg({ text: "", type: "" });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/verify-email-otp`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ otp, userId: user?.user_id }),
      });
      const data = await res.json();
      if (res.ok) {
        onSave({ email: data.newEmail });
        setMsg({ text: "✅ Email updated successfully!", type: "success" });
        setTimeout(() => onClose(), 1500);
      } else {
        setMsg({ text: data.error || "Verification failed.", type: "error" });
      }
    } catch {
      setMsg({ text: "Server error. Try again.", type: "error" });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleBack = () => {
    setOtpSent(false);
    setOtp("");
    setMsg({ text: "", type: "" });
  };

  return (
    <div
      className="epp-overlay"
      /* Only close when clicking the dark backdrop itself */
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchEnd={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`epp-box${isDark ? " dark-mode" : ""}`}
        /* Stop ALL pointer events from bubbling up to the overlay */
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div className="epp-header">
          {otpSent && (
            <button className="epp-back" onClick={handleBack} title="Go back">&#8592;</button>
          )}
          <h3 className="epp-title">{otpSent ? "Verify Email" : "Edit Profile"}</h3>
          <button className="epp-close" onClick={onClose}>&#10005;</button>
        </div>

        <div className="epp-body">

          {/* ══ STEP 1: Edit Name & Email ══ */}
          {!otpSent && (
            <>
              <div className="epp-section">
                <label className="epp-label">Name</label>
                <div className="epp-row">
                  <input
                    type="text"
                    className="epp-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter company name"
                  />
                  <button className="epp-btn-save" onClick={handleSaveName}>Save</button>
                </div>
              </div>

              <div className="epp-divider" />

              <div className="epp-section">
                <label className="epp-label">Email Address</label>
                <div className="epp-row">
                  <input
                    type="email"
                    className="epp-input"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setMsg({ text: "", type: "" }); }}
                    placeholder="Enter new email"
                  />
                  <button
                    className="epp-btn-otp"
                    onClick={handleSendOtp}
                    disabled={emailLoading || !emailChanged}
                  >
                    {emailLoading ? "Sending..." : "Send OTP"}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ══ STEP 2: OTP only ══ */}
          {otpSent && (
            <div className="epp-otp-section">
              <div className="epp-otp-icon">📧</div>
              <p className="epp-otp-title">Check your inbox</p>
              <p className="epp-otp-hint">
                We sent a 6-digit verification code to<br />
                <strong>{email}</strong>
              </p>
              <input
                type="text"
                className="epp-input epp-otp-input"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                placeholder="_ _ _ _ _ _"
                maxLength={6}
                autoFocus
              />
              <button
                className="epp-btn-confirm-full"
                onClick={handleVerifyOtp}
                disabled={emailLoading}
              >
                {emailLoading ? <span className="epp-spinner" /> : "Confirm OTP"}
              </button>
              <button className="epp-resend" onClick={handleSendOtp} disabled={emailLoading}>
                Resend code
              </button>
            </div>
          )}

          {msg.text && (
            <p className={`epp-msg ${msg.type}`}>{msg.text}</p>
          )}

        </div>
      </div>
    </div>
  );
}